/**
 * Connection Store
 *
 * Zustand store for managing connection state in the node editor.
 * Uses the reference key pattern where:
 * - ConnectionReferences: Map of reference keys to actual connections (workflow-level)
 * - ConnectionMapping: Map of node IDs to reference keys (node-level)
 * 
 * For draft mode support, this store tracks its own isDirty and changeCount
 * independent of the workflow store. Autosave aggregates change detection
 * across all artifact stores (workflow, connections, parameters).
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type {
  ConnectionReference,
  ConnectionReferences,
  ConnectionMapping,
  ReferenceKey,
  NodeId,
  AvailableConnection,
  ConnectionStatus,
} from '@/types/connectionReference';
import type { ConnectionsConfiguration, ManagedApiConnection, McpConnection } from '@/types/logicApps';
import {
  extractConnectionReferences,
  extractConnectionMapping,
  resolveConnectionReference,
  generateUniqueReferenceKey,
  validateConnection,
  type ResolvedConnection,
} from '@/services/connectionService';
import { isAgentConnectionId, isManagedApiConnector, isServiceProviderConnector, toBackendAgentConnectionType, normalizeAzureAgentHostUrl } from '@/utils/connections';
import { useWorkflowStore } from './workflowStore';

// =============================================================================
// STATE TYPES
// =============================================================================

interface ConnectionState {
  /** Map of reference keys to connection references (workflow-level) */
  references: ConnectionReferences;
  /** Map of node IDs to reference keys (node-level) */
  mapping: ConnectionMapping;
  /** Available connections for selection */
  availableConnections: AvailableConnection[];
  /** Loading state for available connections */
  isLoadingConnections: boolean;
  /** Error state */
  error: string | null;
  /** Per-connection status cache */
  connectionStatuses: Record<string, ConnectionStatus>;
  /** Per-connection error messages */
  connectionErrors: Record<string, string | null>;
  /** Connections being created (for optimistic UI) */
  pendingCreations: Set<string>;
  /** Connections being deleted */
  pendingDeletions: Set<string>;
  /** Original connections config from connections.json - preserved for saving */
  originalConnectionsConfig: ConnectionsConfiguration | null;
  
  // =============================================================================
  // DIRTY TRACKING (for draft mode)
  // =============================================================================
  
  /** Whether connections have been modified since last save */
  isDirty: boolean;
  /** Counter incremented on every mutation - used for aggregated autosave detection */
  changeCount: number;
}

interface ConnectionActions {
  /**
   * Initialize connection state from workflow and connections config
   */
  initializeFromWorkflow: (
    workflow: { definition?: { triggers?: Record<string, unknown>; actions?: Record<string, unknown> } } | null,
    connections: ConnectionsConfiguration | null
  ) => void;

  /**
   * Set the connection reference for a node
   */
  setNodeConnection: (nodeId: NodeId, referenceKey: ReferenceKey) => void;

  /**
   * Remove the connection from a node
   */
  removeNodeConnection: (nodeId: NodeId) => void;

  /**
   * Add a new connection reference
   */
  addConnectionReference: (referenceKey: ReferenceKey, reference: ConnectionReference) => void;

  /**
   * Update an existing connection reference
   */
  updateConnectionReference: (referenceKey: ReferenceKey, reference: ConnectionReference) => void;

  /**
   * Remove a connection reference (and all node mappings to it)
   */
  removeConnectionReference: (referenceKey: ReferenceKey) => void;

  /**
   * Replace a connection reference key with a new key.
   * Atomically updates references, mapping, and originalConnectionsConfig.
   */
  replaceConnectionKey: (oldKey: ReferenceKey, newKey: ReferenceKey, newRef: ConnectionReference) => void;

  /**
   * Remap every node currently mapped to `oldKey` so it points at `newKey`.
   * Leaves the `references` map untouched — both keys remain in
   * connections.json, only the workflow's node→reference bindings move. Use
   * this from the Connections dialog, where picking a different reference
   * for a connector must rebind affected nodes without destroying any
   * existing connections.json entries.
   */
  remapNodesToReference: (oldKey: ReferenceKey, newKey: ReferenceKey) => void;

  /**
   * Set available connections (from API)
   */
  setAvailableConnections: (connections: AvailableConnection[]) => void;

  /**
   * Set loading state
   */
  setLoadingConnections: (isLoading: boolean) => void;

  /**
   * Set error state
   */
  setError: (error: string | null) => void;

  /**
   * Clear all connection state
   */
  clearConnections: () => void;

  /**
   * Sync references from ConnectionsConfiguration (e.g., from workflow's connections.json)
   */
  syncFromConnectionsConfig: (config: ConnectionsConfiguration | null | undefined) => void;

  /**
   * Rebuild the node→reference `mapping` from a workflow definition.
   *
   * `syncFromConnectionsConfig` only loads `references` (the app-wide
   * connections.json) and intentionally preserves `mapping`, so on a fresh
   * workflow load `mapping` would otherwise stay empty. Call this after loading
   * a workflow so `mapping` reflects which connections THIS workflow actually
   * uses — the workflow-scoped source consumed by the Connections view.
   */
  setMappingFromWorkflow: (
    workflow: { definition?: { triggers?: Record<string, unknown>; actions?: Record<string, unknown> } } | null
  ) => void;

  /**
   * Additively merge BYO MCP connection records into the publish baseline.
   *
   * Unlike {@link syncFromConnectionsConfig} (which does a FULL `references`
   * replacement and is only safe for initial hydration), this merges the given
   * `agentMcpConnections` records into `originalConnectionsConfig` WITHOUT
   * touching `references`. `exportToConnectionsConfig` initializes the
   * `agentMcpConnections` bucket from `originalConnectionsConfig`, so merging
   * the records here is sufficient for them to survive publish — while leaving
   * any unsaved in-memory references (e.g. a managed connection added but not
   * yet persisted) intact.
   *
   * Callers pass the full just-persisted `agentMcpConnections` bucket (not just
   * the one new record) so that records added concurrently (e.g. by another tab
   * and picked up via refetch before save) also land in the baseline.
   */
  mergeMcpConnectionRecords: (records: Record<string, McpConnection>) => void;

  /**
   * Replace the `hostedAgentConnections` bucket of `originalConnectionsConfig`
   * with the given records WITHOUT touching `references`.
   *
   * Hosted agents are created/edited out-of-band by `useSaveHostedAgent` (which
   * writes connections.json directly), while `exportToConnectionsConfig` seeds the
   * `hostedAgentConnections` bucket solely from `originalConnectionsConfig`. Without
   * keeping that baseline in sync, a workflow save after creating a hosted agent
   * would export the stale load-time snapshot and drop the new connection. Callers
   * pass the full post-mutation bucket so create / update / delete all stay in sync
   * (a plain merge could not represent deletions).
   */
  setHostedAgentConnectionRecords: (records: NonNullable<ConnectionsConfiguration['hostedAgentConnections']>) => void;

  /**
   * Get resolved connection for a node
   */
  getNodeConnection: (nodeId: NodeId) => ResolvedConnection | null;

  /**
   * Check if a node has a connection
   */
  hasNodeConnection: (nodeId: NodeId) => boolean;

  /**
   * Validate a node's connection
   */
  validateNodeConnection: (nodeId: NodeId) => { isValid: boolean; error?: string };

  /**
   * Generate a unique reference key
   */
  generateReferenceKey: (connectorName: string) => string;

  /**
   * Export current state as ConnectionsConfiguration for saving
   */
  exportToConnectionsConfig: () => ConnectionsConfiguration;

  // =============================================================================
  // API INTEGRATION ACTIONS
  // =============================================================================

  /**
   * Set connection status for a reference
   */
  setConnectionStatus: (referenceKey: ReferenceKey, status: ConnectionStatus) => void;

  /**
   * Set connection error for a reference
   */
  setConnectionError: (referenceKey: ReferenceKey, error: string | null) => void;

  /**
   * Bulk update connection statuses
   */
  updateConnectionStatuses: (statuses: Record<string, ConnectionStatus>) => void;

  /**
   * Mark a connection as being created
   */
  setPendingCreation: (connectionName: string, pending: boolean) => void;

  /**
   * Mark a connection as being deleted
   */
  setPendingDeletion: (connectionName: string, pending: boolean) => void;

  /**
   * Get connection status by reference key
   */
  getConnectionStatus: (referenceKey: ReferenceKey) => ConnectionStatus | undefined;

  /**
   * Check if a connection is pending creation
   */
  isPendingCreation: (connectionName: string) => boolean;

  /**
   * Check if a connection is pending deletion
   */
  isPendingDeletion: (connectionName: string) => boolean;

  /**
   * Clear all pending operations (useful on error recovery)
   */
  clearPendingOperations: () => void;
  
  // =============================================================================
  // DIRTY TRACKING ACTIONS (for draft mode)
  // =============================================================================
  
  /**
   * Mark connections as dirty (modified since last save)
   */
  markDirty: () => void;
  
  /**
   * Mark connections as clean (after successful save)
   */
  markClean: () => void;
}

type ConnectionStore = ConnectionState & ConnectionActions;

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: ConnectionState = {
  references: {},
  mapping: {},
  availableConnections: [],
  isLoadingConnections: false,
  error: null,
  connectionStatuses: {},
  connectionErrors: {},
  pendingCreations: new Set<string>(),
  pendingDeletions: new Set<string>(),
  originalConnectionsConfig: null,
  // Dirty tracking for draft mode
  isDirty: false,
  changeCount: 0,
};

// =============================================================================
// STORE
// =============================================================================

export const useConnectionStore = create<ConnectionStore>()((set, get) => ({
  ...initialState,

  initializeFromWorkflow: (workflow, connections) => {
    const references = extractConnectionReferences(connections);
    const mapping = extractConnectionMapping(workflow);

    set({
      references,
      mapping,
      error: null,
      // Store original config for preserving full connection data on save
      originalConnectionsConfig: connections ? { ...connections } : null,
    });
  },

  setNodeConnection: (nodeId, referenceKey) => {
    set((state) => ({
      mapping: {
        ...state.mapping,
        [nodeId]: referenceKey,
      },
    }));
    // Mark connections as dirty (for draft mode autosave)
    get().markDirty();
  },

  removeNodeConnection: (nodeId) => {
    set((state) => {
      const newMapping = { ...state.mapping };
      delete newMapping[nodeId];
      return { mapping: newMapping };
    });
    // Mark connections as dirty (for draft mode autosave)
    get().markDirty();
  },

  addConnectionReference: (referenceKey, reference) => {
    set((state) => ({
      references: {
        ...state.references,
        [referenceKey]: reference,
      },
    }));
    // Mark connections as dirty (for draft mode autosave)
    get().markDirty();
  },

  updateConnectionReference: (referenceKey, reference) => {
    set((state) => ({
      references: {
        ...state.references,
        [referenceKey]: reference,
      },
    }));
    // Mark connections as dirty (for draft mode autosave)
    get().markDirty();
  },

  removeConnectionReference: (referenceKey) => {
    set((state) => {
      // Remove the reference
      const newReferences = { ...state.references };
      delete newReferences[referenceKey];

      // Remove all node mappings that use this reference
      const newMapping = { ...state.mapping };
      for (const [nodeId, refKey] of Object.entries(newMapping)) {
        if (refKey === referenceKey) {
          delete newMapping[nodeId];
        }
      }

      return {
        references: newReferences,
        mapping: newMapping,
      };
    });
    // Mark connections as dirty (for draft mode autosave)
    get().markDirty();
  },

  replaceConnectionKey: (oldKey, newKey, newRef) => {
    set((state) => {
      // Update references: remove old, add new
      const newReferences = { ...state.references };
      delete newReferences[oldKey];
      newReferences[newKey] = newRef;

      // Update mapping: swap any node mappings from oldKey to newKey
      const newMapping = { ...state.mapping };
      for (const [nodeId, refKey] of Object.entries(newMapping)) {
        if (refKey === oldKey) {
          newMapping[nodeId] = newKey;
        }
      }

      // Update originalConnectionsConfig: remove oldKey from all connection type sections
      let newOrigConfig = state.originalConnectionsConfig;
      if (newOrigConfig) {
        const updated: ConnectionsConfiguration = { ...newOrigConfig };
        if (updated.managedApiConnections?.[oldKey]) {
          updated.managedApiConnections = { ...updated.managedApiConnections };
          delete updated.managedApiConnections[oldKey];
        }
        if (updated.serviceProviderConnections?.[oldKey]) {
          updated.serviceProviderConnections = { ...updated.serviceProviderConnections };
          delete updated.serviceProviderConnections[oldKey];
        }
        if (updated.agentConnections?.[oldKey]) {
          updated.agentConnections = { ...updated.agentConnections };
          delete updated.agentConnections[oldKey];
        }
        if (updated.agentMcpConnections?.[oldKey]) {
          updated.agentMcpConnections = { ...updated.agentMcpConnections };
          delete updated.agentMcpConnections[oldKey];
        }
        newOrigConfig = updated;
      }

      return {
        references: newReferences,
        mapping: newMapping,
        originalConnectionsConfig: newOrigConfig,
      };
    });
    get().markDirty();
  },

  remapNodesToReference: (oldKey, newKey) => {
    if (!oldKey || !newKey || oldKey === newKey) return;
    const { mapping } = get();
    const affectedNodes = Object.keys(mapping).filter((nodeId) => mapping[nodeId] === oldKey);
    if (affectedNodes.length === 0) return;

    const newMapping = { ...mapping };
    for (const nodeId of affectedNodes) {
      newMapping[nodeId] = newKey;
    }
    set({ mapping: newMapping });
    get().markDirty();
  },

  setAvailableConnections: (connections) => {
    set({ availableConnections: connections });
  },

  setLoadingConnections: (isLoading) => {
    set({ isLoadingConnections: isLoading });
  },

  setError: (error) => {
    set({ error });
  },

  clearConnections: () => {
    set(initialState);
  },

  syncFromConnectionsConfig: (config) => {
    const references = extractConnectionReferences(config);
    set((state) => ({
      // Full replacement: ensures deleted connections are removed, not just new ones added
      references,
      // Preserve mapping — node-to-reference links are managed separately
      mapping: state.mapping,
      // Clear stale config when the workflow has no connections.json, but preserve the
      // previous value when no new config was supplied at all.
      originalConnectionsConfig:
        config === undefined ? state.originalConnectionsConfig : config,
    }));
  },

  setMappingFromWorkflow: (workflow) => {
    set({ mapping: extractConnectionMapping(workflow) });
  },

  mergeMcpConnectionRecords: (records) => {
    set((state) => {
      const prev = state.originalConnectionsConfig ?? {};
      return {
        originalConnectionsConfig: {
          ...prev,
          agentMcpConnections: {
            ...prev.agentMcpConnections,
            ...records,
          },
        },
      };
    });
  },

  setHostedAgentConnectionRecords: (records) => {
    set((state) => {
      const prev = state.originalConnectionsConfig ?? {};
      return {
        originalConnectionsConfig: {
          ...prev,
          hostedAgentConnections: { ...records },
        },
      };
    });
  },

  getNodeConnection: (nodeId) => {
    const { mapping, references } = get();
    const referenceKey = mapping[nodeId];

    if (!referenceKey) return null;

    return resolveConnectionReference(referenceKey, references);
  },

  hasNodeConnection: (nodeId) => {
    const { mapping, references } = get();
    const referenceKey = mapping[nodeId];

    if (!referenceKey) return false;

    return !!references[referenceKey];
  },

  validateNodeConnection: (nodeId) => {
    const { mapping, references } = get();
    const referenceKey = mapping[nodeId];

    if (!referenceKey) {
      return { isValid: false, error: 'No connection assigned' };
    }

    return validateConnection(referenceKey, references);
  },

  generateReferenceKey: (connectorName) => {
    const { references } = get();
    return generateUniqueReferenceKey(Object.keys(references), connectorName);
  },

  exportToConnectionsConfig: () => {
    const { references, originalConnectionsConfig } = get();

    // Start with the original config to preserve all properties
    // This is critical for agent connections which have many properties
    // (endpoint, resourceId, type, modelConfiguration, authentication, etc.)
    // that aren't fully tracked in the ConnectionReference type
    const config: ConnectionsConfiguration = {
      managedApiConnections: { ...originalConnectionsConfig?.managedApiConnections },
      serviceProviderConnections: { ...originalConnectionsConfig?.serviceProviderConnections },
      agentConnections: { ...originalConnectionsConfig?.agentConnections },
      hostedAgentConnections: { ...originalConnectionsConfig?.hostedAgentConnections },
      agentMcpConnections: { ...originalConnectionsConfig?.agentMcpConnections },
    };

    // Now apply any changes from references
    // This handles new connections or updates to existing ones
    for (const [key, ref] of Object.entries(references)) {
      // Determine connection type based on API ID patterns
      if (isManagedApiConnector(ref.api.id)) {
        // Managed API connection (Office 365, SharePoint, etc.)
        // Merge reference updates into existing entries so edits on previously
        // persisted managed connections (for example adding connectionProperties)
        // are not dropped.
        const existingManaged = config.managedApiConnections![key];
        const mergedManagedEntry: ManagedApiConnection = {
          ...(existingManaged ?? {}),
          api: ref.api,
          connection: ref.connection,
          connectionRuntimeUrl: ref.connectionRuntimeUrl ?? existingManaged?.connectionRuntimeUrl ?? '',
          // Workflow → api-hub gateway auth: always MI (Logic Apps Standard convention;
          // matches LogicAppsUX `getAuthenticationDetails`). Routing decisions for
          // dynamicInvoke don't read this field — they use `useManagedConnectionInfo`,
          // which classifies from the live connection ARM resource.
          authentication: ref.authentication ?? existingManaged?.authentication ?? { type: 'ManagedServiceIdentity' },
          // Persist the user-facing name so it survives reload and is
          // visible to other tools reading connections.json. Only write
          // when it differs from the key to keep round-tripping clean.
          ...(ref.connectionName && ref.connectionName !== key
            ? { displayName: ref.connectionName }
            : existingManaged?.displayName
              ? { displayName: existingManaged.displayName }
              : {}),
          ...(existingManaged?.connectionProperties
            ? { connectionProperties: { ...existingManaged.connectionProperties } }
            : {}),
        };

        if (ref.connectionProperties && Object.keys(ref.connectionProperties).length > 0) {
          mergedManagedEntry.connectionProperties = {
            ...mergedManagedEntry.connectionProperties,
            ...ref.connectionProperties,
          };
        }

        config.managedApiConnections![key] = mergedManagedEntry;
      } else if (isServiceProviderConnector(ref.api.id)) {
        // Service provider connection (Service Bus, Event Hubs, etc.)
        if (!config.serviceProviderConnections![key]) {
          const props = (ref.connectionProperties ?? {}) as Record<string, unknown>;
          // Extract _parameterSetName (internal marker) from connectionProperties
          const { _parameterSetName, ...parameterValues } = props;
          config.serviceProviderConnections![key] = {
            serviceProvider: { id: ref.api.id },
            displayName: ref.connectionName,
            ...(typeof _parameterSetName === 'string' ? { parameterSetName: _parameterSetName } : {}),
            parameterValues: parameterValues as Record<string, string>,
          };
        }
      } else if (isAgentConnectionId(ref.api.id)) {
        // Hosted agent connections are managed by useSaveHostedAgent directly and
        // preserved from originalConnectionsConfig above — skip re-serialization.
        if (ref.api.id.toLowerCase().includes('/hostedagentconnections/')) {
          continue;
        }
        // Agent connection (Azure OpenAI, OpenAI, Foundry, etc.)
        // For agent connections, preserve the original if it exists
        // as it contains critical properties like endpoint, resourceId, type
        if (!config.agentConnections![key]) {
          // New agent connection - construct from reference
          const props = ref.connectionProperties as Record<string, unknown> | undefined;
          const modelConfig = props?.modelConfiguration as {
            deploymentName?: string;
            modelName?: string;
            apiVersion?: string;
          } | undefined;

          const newAgentConn: import('@/types/logicApps').AgentConnection = {
            displayName: ref.connectionName ?? key,
          };

          // Derive modelProvider from api.id
          // Pattern: /agentConnections/AzureOpenAI or /connectionProviders/agent
          if (ref.api.id) {
            newAgentConn.modelProvider = {
              id: ref.api.id,
            };
          }

          if (modelConfig) {
            newAgentConn.modelConfiguration = modelConfig;
          }
          if (props?.endpoint) {
            newAgentConn.endpoint = props.endpoint as string;
          }
          if (props?.resourceId) {
            newAgentConn.resourceId = props.resourceId as string;
          }
          if (props?.type) {
            // Map the granular client provider name (e.g. 'AzureOpenAI',
            // 'FoundryAgentServiceV2') to the backend AgentConnectionType enum
            // (`Model` | `FoundryAgentService` | `APIMGenAIGateway`). The
            // original granular value remains on `connectionProperties.type`
            // in-memory so `getAgentReferenceProvider` can still discriminate.
            newAgentConn.type = toBackendAgentConnectionType(props.type as string);
          }
          // Handle authentication - prefer props.authentication over ref.authentication
          // as props.authentication may contain the full auth config including key
          if (props?.authentication) {
            newAgentConn.authentication = props.authentication as { type: string; identity?: string; key?: string };
          } else if (ref.authentication) {
            newAgentConn.authentication = ref.authentication;
          }
          config.agentConnections![key] = newAgentConn;
        }
        // If it exists in original, it's already in config - don't overwrite
      } else if (ref.api.id.includes('/mcpConnections/') || ref.api.id.includes('/mcp/')) {
        // MCP connection. NOTE: `/mcpConnections/` here is a portal-internal URI
        // discriminator minted by `configToReferences` — it is never serialized.
        // The serialized bucket is `agentMcpConnections` (runtime contract).
        if (!config.agentMcpConnections![key]) {
          const props = ref.connectionProperties as Record<string, unknown> | undefined;
          config.agentMcpConnections![key] = {
            mcpServerUrl: (props?.endpoint as string) ?? '',
            authentication: (ref.authentication as Record<string, unknown> | undefined) ?? null,
            ...(ref.connectionName ? { displayName: ref.connectionName } : {}),
            ...(props?.parameterValues ? { parameterValues: props.parameterValues as Record<string, unknown> } : {}),
          };
        }
      }
    }

    // Clean up empty connection objects
    if (!config.managedApiConnections || Object.keys(config.managedApiConnections).length === 0) {
      delete config.managedApiConnections;
    }
    if (!config.serviceProviderConnections || Object.keys(config.serviceProviderConnections).length === 0) {
      delete config.serviceProviderConnections;
    }
    if (!config.agentConnections || Object.keys(config.agentConnections).length === 0) {
      delete config.agentConnections;
    }
    if (!config.hostedAgentConnections || Object.keys(config.hostedAgentConnections).length === 0) {
      delete config.hostedAgentConnections;
    }
    if (!config.agentMcpConnections || Object.keys(config.agentMcpConnections).length === 0) {
      delete config.agentMcpConnections;
    }

    // Backfill azureAgentAIResourceName for azureagentservice managed connections
    // that are missing it. The value is the AI Foundry account name, derived from
    // the workflow definition's action inputs (e.g. inputs.parameters.endpoint or
    // inputs.host.connection.referenceName matching this key).
    if (config.managedApiConnections) {
      // Read workflow actions to find which action uses each connection reference
      let workflowActions: Record<string, unknown> | undefined;
      try {
        const workflow = useWorkflowStore.getState().workflow;
        workflowActions = (workflow?.definition?.actions ?? {}) as Record<string, unknown>;
      } catch { /* workflowStore not ready */ }

      // If there is exactly one known resource name across azureagentservice
      // connections, we can safely reuse it for sibling entries that are missing it.
      const knownAzureAgentResourceNames = new Set(
        Object.values(config.managedApiConnections)
          .filter((conn) => conn.api.id.toLowerCase().includes('azureagentservice'))
          .map((conn) => conn.connectionProperties?.azureAgentAIResourceName)
          .filter((name): name is string => typeof name === 'string' && name.length > 0),
      );
      const singleKnownAzureAgentResourceName = knownAzureAgentResourceNames.size === 1
        ? Array.from(knownAzureAgentResourceNames)[0]
        : undefined;

      for (const [key, conn] of Object.entries(config.managedApiConnections)) {
        if (!conn.api.id.toLowerCase().includes('azureagentservice')) continue;
        const existingAzureAgentValue = conn.connectionProperties?.azureAgentAIResourceName;
        if (typeof existingAzureAgentValue === 'string' && existingAzureAgentValue.length > 0) {
          const normalizedExisting = normalizeAzureAgentHostUrl(existingAzureAgentValue);
          if (normalizedExisting) {
            conn.connectionProperties = {
              ...conn.connectionProperties,
              azureAgentAIResourceName: normalizedExisting,
            };
          }
          continue;
        }

        // Try to find this connection's endpoint from matching workflow actions
        if (workflowActions) {
          const findEndpointForRef = (actions: Record<string, unknown>): string | undefined => {
            for (const action of Object.values(actions)) {
              const a = action as Record<string, unknown>;
              const host = a.host as Record<string, unknown> | undefined;
              const refName = (host?.connection as Record<string, unknown> | undefined)?.referenceName;
              if (refName === key) {
                // Found the action using this connection — extract endpoint
                const inputs = a.inputs as Record<string, unknown> | undefined;
                const params = inputs?.parameters as Record<string, unknown> | undefined;
                const ep = (params?.endpoint ?? inputs?.endpoint) as string | undefined;
                if (ep) return ep;
              }
              // Recurse into nested actions
              const nested = (a.actions ?? (a.else as Record<string, unknown>)?.actions) as Record<string, unknown> | undefined;
              if (nested) {
                const found = findEndpointForRef(nested);
                if (found) return found;
              }
            }
            return undefined;
          };

          const endpoint = findEndpointForRef(workflowActions);
          if (endpoint) {
            try {
              const normalizedEndpoint = normalizeAzureAgentHostUrl(endpoint);
              if (normalizedEndpoint) {
                conn.connectionProperties = {
                  ...conn.connectionProperties,
                  azureAgentAIResourceName: normalizedEndpoint,
                };
                continue;
              }
            } catch { /* invalid endpoint URL */ }
          }
        }

        if (singleKnownAzureAgentResourceName) {
          const normalizedKnown = normalizeAzureAgentHostUrl(singleKnownAzureAgentResourceName);
          if (!normalizedKnown) continue;
          conn.connectionProperties = {
            ...conn.connectionProperties,
            azureAgentAIResourceName: normalizedKnown,
          };
        }
      }
    }

    return config;
  },

  // =============================================================================
  // API INTEGRATION ACTION IMPLEMENTATIONS
  // =============================================================================

  setConnectionStatus: (referenceKey, status) => {
    set((state) => ({
      connectionStatuses: {
        ...state.connectionStatuses,
        [referenceKey]: status,
      },
    }));
  },

  setConnectionError: (referenceKey, error) => {
    set((state) => ({
      connectionErrors: {
        ...state.connectionErrors,
        [referenceKey]: error,
      },
    }));
  },

  updateConnectionStatuses: (statuses) => {
    set((state) => ({
      connectionStatuses: {
        ...state.connectionStatuses,
        ...statuses,
      },
    }));
  },

  setPendingCreation: (connectionName, pending) => {
    set((state) => {
      const newPending = new Set(state.pendingCreations);
      if (pending) {
        newPending.add(connectionName);
      } else {
        newPending.delete(connectionName);
      }
      return { pendingCreations: newPending };
    });
  },

  setPendingDeletion: (connectionName, pending) => {
    set((state) => {
      const newPending = new Set(state.pendingDeletions);
      if (pending) {
        newPending.add(connectionName);
      } else {
        newPending.delete(connectionName);
      }
      return { pendingDeletions: newPending };
    });
  },

  getConnectionStatus: (referenceKey) => {
    return get().connectionStatuses[referenceKey];
  },

  isPendingCreation: (connectionName) => {
    return get().pendingCreations.has(connectionName);
  },

  isPendingDeletion: (connectionName) => {
    return get().pendingDeletions.has(connectionName);
  },

  clearPendingOperations: () => {
    set({
      pendingCreations: new Set<string>(),
      pendingDeletions: new Set<string>(),
    });
  },
  
  // =============================================================================
  // DIRTY TRACKING (for draft mode)
  // =============================================================================
  
  markDirty: () => {
    set((state) => ({
      isDirty: true,
      changeCount: state.changeCount + 1,
    }));
  },
  
  markClean: () => {
    set({
      isDirty: false,
      // Note: changeCount is NOT reset - it's cumulative for autosave detection
    });
  },
}));

// =============================================================================
// SELECTOR HOOKS
// =============================================================================

/**
 * Get all reference keys
 */
export const useConnectionReferenceKeys = () =>
  useConnectionStore(useShallow((state) => Object.keys(state.references)));

/**
 * Get mapping for a specific node
 */
export const useNodeReferenceKey = (nodeId: NodeId) =>
  useConnectionStore((state) => state.mapping[nodeId] ?? null);

/**
 * Get connection reference by key
 */
export const useConnectionReference = (referenceKey: ReferenceKey) =>
  useConnectionStore((state) => state.references[referenceKey]);

/**
 * Get connection status by reference key
 */
export const useConnectionStatusByKey = (referenceKey: ReferenceKey) =>
  useConnectionStore((state) => state.connectionStatuses[referenceKey]);

/**
 * Get connection error by reference key
 */
export const useConnectionErrorByKey = (referenceKey: ReferenceKey) =>
  useConnectionStore((state) => state.connectionErrors[referenceKey]);

/**
 * Check if any connections are pending operations
 */
export const useHasPendingOperations = () =>
  useConnectionStore(
    useShallow((state) =>
      state.pendingCreations.size > 0 || state.pendingDeletions.size > 0
    )
  );

/**
 * Get all connection statuses
 */
export const useConnectionStatuses = () =>
  useConnectionStore(useShallow((state) => state.connectionStatuses));

// =============================================================================
// DIRTY TRACKING SELECTORS
// =============================================================================

/**
 * Check if connections have unsaved changes
 */
export const useConnectionsIsDirty = () =>
  useConnectionStore((state) => state.isDirty);

/**
 * Get connection change count (for aggregated autosave detection)
 */
export const useConnectionsChangeCount = () =>
  useConnectionStore((state) => state.changeCount);

/**
 * Selectors for direct store access (non-hook)
 */
export const selectConnectionsIsDirty = (state: ConnectionStore) => state.isDirty;
export const selectConnectionsChangeCount = (state: ConnectionStore) => state.changeCount;
